import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { useToast } from '../../hooks/use-toast';
import api, { formatApiError, fileUrl, getInitials } from '../../lib/api';
import { Search, Plus, Loader2, UserPlus, Camera, KeyRound, Copy } from 'lucide-react';

const SKILLS = ['beginner', 'intermediate', 'advanced', 'competitive'];
const AGES = ['infant', 'kids', 'teen', 'adult', 'senior'];
const GENDERS = ['male', 'female', 'other'];
const LEAD_SOURCES = ['Walk-in', 'Reference', 'Social Media', 'Flyer', 'Other'];

const skillColor = (s) => ({ beginner: 'bg-gray-100 text-gray-700', intermediate: 'bg-blue-100 text-blue-700', advanced: 'bg-purple-100 text-purple-700', competitive: 'bg-amber-100 text-amber-700' }[s] || 'bg-gray-100 text-gray-700');

const emptyForm = { name: '', phone: '', email: '', dob: '', gender: '', age_group: 'adult', skill_level: 'beginner', blood_group: '', profile_pic_url: '', lead_source: '', sales_rep_id: '' };

const MembersPage = () => {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [reps, setReps] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [creds, setCreds] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const res = await api.get('/swimmers', { params: { search: q || undefined, limit: 50 } });
      setMembers(res.data);
    } catch (err) {
      toast({ title: 'Failed to load members', description: formatApiError(err), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/admin/users', { params: { limit: 100 } })
      .then((r) => setReps(r.data.filter((u) => ['admin', 'staff', 'coach'].includes(u.role))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 350);
    return () => clearTimeout(t);
  }, [search, load]);

  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target ? e.target.value : e }));

  const openNew = () => { setForm(emptyForm); setPreview(null); setOpen(true); };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post('/files/upload', fd);
      setForm((p) => ({ ...p, profile_pic_url: res.data.path }));
      setPreview(URL.createObjectURL(f));
      toast({ title: 'Photo uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: formatApiError(err), variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast({ title: 'Name is required', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
      const res = await api.post('/swimmers', payload);
      toast({ title: 'Member added! 🎉', description: `${res.data.name} · ${res.data.swimmer_id_code}` });
      setOpen(false);
      setForm(emptyForm);
      setPreview(null);
      if (res.data.portal_username && res.data.portal_password) {
        setCreds({ name: res.data.name, username: res.data.portal_username, password: res.data.portal_password });
      }
      load();
    } catch (err) {
      toast({ title: 'Failed to add member', description: formatApiError(err), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-members">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Members</h1>
          <p className="text-gray-500 text-sm mt-1">{members.length} member{members.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={openNew} className="bg-gradient-to-r from-blue-600 to-cyan-600" data-testid="new-member-btn">
          <Plus className="mr-2 h-4 w-4" /> New Member
        </Button>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, code or phone" className="pl-9" data-testid="members-search" />
      </div>

      <Card className="shadow-md border-0 bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
          ) : members.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="members-empty">No members found. Add your first member.</div>
          ) : (
            <Table data-testid="members-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Skill</TableHead>
                  <TableHead>Lead Source</TableHead>
                  <TableHead>Sales Rep</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.id} className="cursor-pointer" onClick={() => navigate(`/admin-dashboard/members/${m.id}`)} data-testid={`member-row-${m.swimmer_id_code}`}>
                    <TableCell>
                      <Avatar className="h-9 w-9">
                        {m.profile_pic_url && <AvatarImage src={fileUrl(m.profile_pic_url)} alt={m.name} />}
                        <AvatarFallback className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xs">{getInitials(m.name)}</AvatarFallback>
                      </Avatar>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{m.swimmer_id_code}</TableCell>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell><Badge className={skillColor(m.skill_level)}>{m.skill_level}</Badge></TableCell>
                    <TableCell className="text-gray-600">{m.lead_source || '—'}</TableCell>
                    <TableCell className="text-gray-600">{m.sales_rep_name || '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/admin-dashboard/members/${m.id}`); }}>View</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="new-member-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5" /> New Member</DialogTitle>
            <DialogDescription>Register a new swimmer with their profile details.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 flex items-center gap-4">
              <Avatar className="h-16 w-16">
                {preview ? <AvatarImage src={preview} alt="preview" /> : null}
                <AvatarFallback className="bg-gray-100 text-gray-400"><Camera className="h-5 w-5" /></AvatarFallback>
              </Avatar>
              <div>
                <input id="member-photo" type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="member-photo-input" />
                <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => document.getElementById('member-photo').click()} data-testid="member-photo-btn">
                  {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}
                  {form.profile_pic_url ? 'Change Photo' : 'Upload Photo'}
                </Button>
                <p className="text-xs text-gray-400 mt-1">JPG/PNG/WebP, max 5MB</p>
              </div>
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Full Name *</Label>
              <Input value={form.name} onChange={upd('name')} placeholder="Member name" data-testid="member-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={upd('phone')} placeholder="Phone" data-testid="member-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={form.email} onChange={upd('email')} placeholder="Email" data-testid="member-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Date of Birth</Label>
              <Input type="date" value={form.dob} onChange={upd('dob')} data-testid="member-dob-input" />
            </div>
            <div className="space-y-2">
              <Label>Gender</Label>
              <Select value={form.gender} onValueChange={(v) => setForm((p) => ({ ...p, gender: v }))}>
                <SelectTrigger data-testid="member-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{GENDERS.map((g) => (<SelectItem key={g} value={g} className="capitalize">{g}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Age Group</Label>
              <Select value={form.age_group} onValueChange={(v) => setForm((p) => ({ ...p, age_group: v }))}>
                <SelectTrigger data-testid="member-age-select"><SelectValue /></SelectTrigger>
                <SelectContent>{AGES.map((a) => (<SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Skill Level</Label>
              <Select value={form.skill_level} onValueChange={(v) => setForm((p) => ({ ...p, skill_level: v }))}>
                <SelectTrigger data-testid="member-skill-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SKILLS.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Lead Source</Label>
              <Select value={form.lead_source} onValueChange={(v) => setForm((p) => ({ ...p, lead_source: v }))}>
                <SelectTrigger data-testid="member-lead-select"><SelectValue placeholder="How did they find us?" /></SelectTrigger>
                <SelectContent>{LEAD_SOURCES.map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sales Representative</Label>
              <Select value={form.sales_rep_id} onValueChange={(v) => setForm((p) => ({ ...p, sales_rep_id: v }))}>
                <SelectTrigger data-testid="member-rep-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>
                  {reps.length === 0 ? <SelectItem value="none" disabled>No staff found</SelectItem>
                    : reps.map((r) => (<SelectItem key={r.id} value={r.id}>{r.name} ({r.role})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Blood Group</Label>
              <Input value={form.blood_group} onChange={upd('blood_group')} placeholder="e.g. O+" data-testid="member-blood-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-gradient-to-r from-blue-600 to-cyan-600" data-testid="member-save-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Portal credentials dialog (shown once after creation) */}
      <Dialog open={!!creds} onOpenChange={() => setCreds(null)}>
        <DialogContent className="sm:max-w-md" data-testid="creds-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-cyan-600" /> Client Login Created</DialogTitle>
            <DialogDescription>Share these credentials with {creds?.name}. The password is shown only once.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 flex items-center justify-between">
              <div><p className="text-xs text-gray-400">Login ID</p><p className="font-mono font-semibold text-gray-900" data-testid="creds-username">{creds?.username}</p></div>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { navigator.clipboard?.writeText(creds?.username || ''); toast({ title: 'Login ID copied' }); }}><Copy className="h-4 w-4" /></Button>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 flex items-center justify-between">
              <div><p className="text-xs text-gray-400">Password</p><p className="font-mono font-semibold text-gray-900" data-testid="creds-password">{creds?.password}</p></div>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { navigator.clipboard?.writeText(creds?.password || ''); toast({ title: 'Password copied' }); }}><Copy className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-gray-500">The client can log in from the site using this Login ID and password to track classes, bookings & progress.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreds(null)} className="bg-gradient-to-r from-blue-600 to-cyan-600" data-testid="creds-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembersPage;
